import React, { useEffect, useState } from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import {
	Autocomplete,
	TextField,
	Box,
	Button,
	Typography,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	IconButton,
	Modal,
	Fab,
	useMediaQuery,
	Card,
	CardContent,
	Snackbar,
	Alert,
	Select,
	MenuItem,
	FormControl,
	InputLabel,
	Link,
	InputAdornment
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import api from "../utils/api";
import NavBar from "../components/NavBar";
import ProfitabilityCard from "../components/ProfitabilityCard";
import { ProfitabilityMetrics } from "../types";

interface Ingredient {
	Id: string;
	name: string;
	rawQuantity: string;
	quantity: string;
	measurementUnit: string;
}

interface Meal {
	Id: string;
	name: string;
	sellPrice?: number;
	wholesalePrice?: number;
	ingredients: MealIngredient[];
	profitability?: ProfitabilityMetrics | null;
}

interface MealIngredient {
	mealId: string;
	ingredientId: string;
	ingredientName: string;
	Id: string;
	rawQuantity: string;
	quantity: string;
	measurementUnit: string;
	ingredientCost: number | null;
}

const MealDetailPage: React.FC = () => {
	const { id } = useParams<{ id: string }>();
	const [meal, setMeal] = useState<Meal | null>(null);
	const [ingredients, setIngredients] = useState<Ingredient[]>([]);
	const [newIngredient, setNewIngredient] = useState<Ingredient | null>(null);
	const [rawQuantity, setRawQuantity] = useState("");
	const [cookedQuantity, setCookedQuantity] = useState("");
	const [measurementUnit, setUnit] = useState("grams");
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isEditMode, setIsEditMode] = useState(false);
	const [editingIngredientId, setEditingIngredientId] = useState<string | null>(null);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [ingredientToDelete, setIngredientToDelete] = useState<MealIngredient | null>(null);
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({
		open: false,
		message: "",
		severity: "success"
	});

	// State for meal edit modal
	const [isEditMealModalOpen, setIsEditMealModalOpen] = useState(false);
	const [editMealName, setEditMealName] = useState("");
	const [editSellPrice, setEditSellPrice] = useState<string>("");
	const [editWholesalePrice, setEditWholesalePrice] = useState<string>("");

	useEffect(() => {
		const fetchMeal = async () => {
			try {
				const response = await api.get<Meal>(`/meals/${id}`);
				setMeal(response.data);
			} catch (error) {
				console.error("Failed to fetch meal details", error);
			}
		};

		const fetchIngredients = async () => {
			try {
				const response = await api.get<Ingredient[]>("/ingredients");
				setIngredients(response.data);
			} catch (error) {
				console.error("Failed to fetch ingredients", error);
			}
		};

		fetchMeal();
		fetchIngredients();
	}, [id]);

	const handleAddIngredient = async () => {
		if (!newIngredient || !rawQuantity.trim()) {
			setSnackbar({ open: true, message: "Please select an ingredient and enter the amount", severity: "error" });
			return;
		}
		if (measurementUnit !== "qty" && !cookedQuantity.trim()) {
			setSnackbar({ open: true, message: "Please enter both raw and cooked weight", severity: "error" });
			return;
		}

		try {
			// Use both rawQuantity and cookedQuantity
			const mealIngredientData = {
				mealId: id,
				ingredientId: newIngredient.Id,
				ingredientName: newIngredient.name,
				rawQuantity: rawQuantity,
				quantity: cookedQuantity,
				measurementUnit
			};

			if (isEditMode && editingIngredientId) {
				await api.put(`/meals/${id}/ingredients/${editingIngredientId}`, mealIngredientData);
				setSnackbar({ open: true, message: "Ingredient updated successfully", severity: "success" });
			} else {
				await api.post(`/meals/${id}/ingredients`, mealIngredientData);
				setSnackbar({ open: true, message: "Ingredient added successfully", severity: "success" });
			}

			const response = await api.get<Meal>(`/meals/${id}`);
			setMeal(response.data);
			resetForm();
		} catch (error) {
			console.error("Failed to add/update ingredient", error);
			setSnackbar({ open: true, message: "Failed to add/update ingredient", severity: "error" });
		}
	};

	const handleEditIngredient = (ingredient: MealIngredient) => {
		setIsEditMode(true);
		setEditingIngredientId(ingredient.ingredientId);
		setNewIngredient({
			Id: ingredient.ingredientId,
			name: ingredient.ingredientName,
			rawQuantity: ingredient.rawQuantity,
			quantity: ingredient.quantity,
			measurementUnit: ingredient.measurementUnit
		});
		setRawQuantity(ingredient.rawQuantity);
		setCookedQuantity(ingredient.quantity);
		setUnit(ingredient.measurementUnit);
		setIsModalOpen(true);
	};

	const handleRemoveIngredient = (ingredient: MealIngredient) => {
		setIngredientToDelete(ingredient);
		setIsDeleteModalOpen(true);
	};

	const confirmDeleteIngredient = async () => {
		if (!meal || !ingredientToDelete) return;

		try {
			await api.delete(`/meals/${meal.Id}/ingredients/${ingredientToDelete.ingredientId}`);
			const response = await api.get<Meal>(`/meals/${id}`);
			setMeal(response.data);
			setSnackbar({ open: true, message: "Ingredient removed successfully", severity: "success" });
		} catch (error) {
			console.error("Failed to remove ingredient", error);
			setSnackbar({ open: true, message: "Failed to remove ingredient", severity: "error" });
		} finally {
			setIsDeleteModalOpen(false);
			setIngredientToDelete(null);
		}
	};

	const handleCloseSnackbar = () => {
		setSnackbar({ ...snackbar, open: false });
	};

	const resetForm = () => {
		setNewIngredient(null);
		setRawQuantity("");
		setCookedQuantity("");
		setUnit("grams");
		setIsModalOpen(false);
		setIsEditMode(false);
		setEditingIngredientId(null);
	};

	const handleOpenEditMealModal = () => {
		if (meal) {
			setEditMealName(meal.name);
			setEditSellPrice(meal.sellPrice !== undefined ? meal.sellPrice.toString() : "");
			setEditWholesalePrice(meal.wholesalePrice !== undefined ? meal.wholesalePrice.toString() : "");
			setIsEditMealModalOpen(true);
		}
	};

	const handleCloseEditMealModal = () => {
		setIsEditMealModalOpen(false);
		setEditMealName("");
		setEditSellPrice("");
		setEditWholesalePrice("");
	};

	const handleSubmitEditMeal = async () => {
		const formattedMealName = editMealName.trim();
		if (!formattedMealName) {
			setSnackbar({
				open: true,
				message: "Please enter a meal name",
				severity: "error"
			});
			return;
		}

		const mealData: any = { name: formattedMealName };

		// Add sellPrice if provided
		if (editSellPrice) {
			const parsedSellPrice = parseFloat(editSellPrice);
			if (isNaN(parsedSellPrice) || parsedSellPrice < 0) {
				setSnackbar({
					open: true,
					message: "Please enter a valid sell price",
					severity: "error"
				});
				return;
			}
			mealData.sellPrice = parsedSellPrice;
		}

		// Add wholesalePrice if provided
		if (editWholesalePrice) {
			const parsedWholesalePrice = parseFloat(editWholesalePrice);
			if (isNaN(parsedWholesalePrice) || parsedWholesalePrice < 0) {
				setSnackbar({
					open: true,
					message: "Please enter a valid wholesale price",
					severity: "error"
				});
				return;
			}
			mealData.wholesalePrice = parsedWholesalePrice;
		}

		try {
			await api.put(`/meals/${id}`, mealData);
			setSnackbar({ open: true, message: "Meal updated successfully", severity: "success" });

			// Refetch meal details
			const response = await api.get<Meal>(`/meals/${id}`);
			if (response.data) {
				setMeal(response.data);
			}
			handleCloseEditMealModal();
		} catch (error) {
			console.error("Failed to update meal", error);
			setSnackbar({ open: true, message: "Failed to update meal", severity: "error" });
		}
	};

	if (!meal) {
		return <Typography>Loading...</Typography>;
	}

	return (
		<>
			<NavBar />
			<Box p={3}>
				<Box display="flex" alignItems="center" mb={2}>
					<Typography variant="h4" sx={{ flex: 1 }}>
						{meal.name}
					</Typography>
					<IconButton
						aria-label="edit meal"
						onClick={handleOpenEditMealModal}
						color="primary">
						<EditIcon />
					</IconButton>
				</Box>
				<Typography variant="h5" gutterBottom>
					Ingredients
				</Typography>

				{isMobile ? (
					// Convert to cards on mobile
					<Box display="flex" flexDirection="column" gap={2}>
						{meal.ingredients.map((ingredient) => (
							<Card key={ingredient.Id} sx={{ p: 2, borderRadius: 2, boxShadow: 2 }}>
								<CardContent>
									<Box display="flex" justifyContent="space-between" alignItems="center">
										<Box>
											<Typography variant="body1" fontWeight="bold">
												Name:
											</Typography>
											<Link
												component={RouterLink}
												to={`/ingredients/${ingredient.ingredientId}`}
												underline="hover"
												color="inherit">
												<Typography variant="subtitle1" gutterBottom>
													{ingredient.ingredientName}
												</Typography>
											</Link>
										</Box>
										<Box display="flex">
											<IconButton
												aria-label="edit"
												onClick={() => handleEditIngredient(ingredient)}
												sx={{ color: "black", mr: 1 }}>
												<EditIcon />
											</IconButton>
											<IconButton
												aria-label="delete"
												onClick={() => handleRemoveIngredient(ingredient)}
												sx={{ color: "black" }}>
												<DeleteIcon />
											</IconButton>
										</Box>
									</Box>
									<Box display="flex" justifyContent="flex-start" gap={4} mt={2}>
										{ingredient.measurementUnit === "qty" ? (
											<Box>
												<Typography variant="body2" fontWeight="bold">
													Quantity:
												</Typography>
												<Typography variant="body2">
													{ingredient.rawQuantity}
												</Typography>
											</Box>
										) : (
											<>
												<Box>
													<Typography variant="body2" fontWeight="bold">
														Raw Weight:
													</Typography>
													<Typography variant="body2">
														{ingredient.rawQuantity} {ingredient.measurementUnit}
													</Typography>
												</Box>
												<Box>
													<Typography variant="body2" fontWeight="bold">
														Cooked Weight:
													</Typography>
													<Typography variant="body2">
														{ingredient.quantity} {ingredient.measurementUnit}
													</Typography>
												</Box>
											</>
										)}
										<Box>
											<Typography variant="body2" fontWeight="bold">
												Cost:
											</Typography>
											<Typography variant="body2">
												{ingredient.ingredientCost !== null
													? `$${ingredient.ingredientCost.toFixed(2)}`
													: "-"}
											</Typography>
										</Box>
									</Box>
								</CardContent>
							</Card>
						))}
					</Box>
				) : (
					// Display as table on larger screens
					<TableContainer component={Paper}>
						<Table>
							<TableHead>
								<TableRow>
									<TableCell>
										<b>Name</b>
									</TableCell>
									<TableCell>
										<b>Amount</b>
									</TableCell>
									<TableCell>
										<b>Cost</b>
									</TableCell>
									<TableCell>
										<b>Actions</b>
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{meal.ingredients.length > 0 ? (
									meal.ingredients.map((ingredient) => (
										<TableRow key={ingredient.Id}>
											<TableCell>
												<Link
													component={RouterLink}
													to={`/ingredients/${ingredient.ingredientId}`}
													underline="hover">
													{ingredient.ingredientName}
												</Link>
											</TableCell>
											<TableCell>
												{ingredient.measurementUnit === "qty" ? (
													ingredient.rawQuantity
												) : (
													<>
														{ingredient.rawQuantity} {ingredient.measurementUnit} (raw) / {ingredient.quantity} {ingredient.measurementUnit} (cooked)
													</>
												)}
											</TableCell>
											<TableCell>
												{ingredient.ingredientCost !== null
													? `$${ingredient.ingredientCost.toFixed(2)}`
													: "-"}
											</TableCell>
											<TableCell>
												<IconButton edge="end" aria-label="edit" onClick={() => handleEditIngredient(ingredient)}>
													<EditIcon />
												</IconButton>
												<IconButton edge="end" aria-label="delete" onClick={() => handleRemoveIngredient(ingredient)}>
													<DeleteIcon />
												</IconButton>
											</TableCell>
										</TableRow>
									))
								) : (
									<TableRow>
										<TableCell colSpan={4}>
											<Typography>No ingredients found</Typography>
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					</TableContainer>
				)}

				{/* Profitability Card - Display below ingredients table */}
				<ProfitabilityCard profitability={meal.profitability || null} hasSellPrice={!!meal.sellPrice} />

				{/* Confirmation modal for deleting ingredient */}
				<Modal open={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)}>
					<Box p={3} bgcolor="background.paper" borderRadius={2} mx="auto" my="10vh" width="80vw" maxWidth={400} textAlign="center">
						<Typography variant="h5" gutterBottom>
							Confirm Delete
						</Typography>
						<Typography variant="body1" gutterBottom>
							Are you sure you want to delete this ingredient from the meal?
						</Typography>
						<Box display="flex" justifyContent="space-between" mt={3}>
							<Button variant="outlined" onClick={() => setIsDeleteModalOpen(false)}>
								Cancel
							</Button>
							<Button variant="contained" color="error" onClick={confirmDeleteIngredient}>
								Delete
							</Button>
						</Box>
					</Box>
				</Modal>

				<Fab color="primary" aria-label="add" onClick={() => setIsModalOpen(true)} sx={{ position: "fixed", bottom: 16, right: 16 }}>
					<AddIcon />
				</Fab>

				<Modal open={isModalOpen} onClose={resetForm}>
					<Box p={3} bgcolor="background.paper" borderRadius={2} mx="auto" my="10vh" width="80vw" maxWidth={400} textAlign="center">
						<Typography variant="h5" gutterBottom>
							{isEditMode ? "Edit Ingredient" : "Add New Ingredient"}
						</Typography>
						<Box display="flex" flexDirection="column" gap={2}>
							<Autocomplete
								options={ingredients}
								getOptionLabel={(option) => option.name}
								value={newIngredient}
								onChange={(event, newValue) => setNewIngredient(newValue)}
								renderInput={(params) => <TextField {...params} label="Select Ingredient" variant="outlined" />}
							/>
							<FormControl variant="outlined" fullWidth>
								<InputLabel>Unit</InputLabel>
								<Select sx={{ textAlign: "left" }} value={measurementUnit} onChange={(e) => setUnit(e.target.value)} label="Unit">
									<MenuItem value="grams">grams</MenuItem>
									<MenuItem value="ml">ml</MenuItem>
									<MenuItem value="qty">qty</MenuItem>
								</Select>
							</FormControl>
							{measurementUnit === "qty" ? (
								<TextField
									label="Quantity"
									type="number"
									value={rawQuantity}
									onChange={(e) => {
										setRawQuantity(e.target.value);
										setCookedQuantity(e.target.value);
									}}
									variant="outlined"
									inputProps={{ step: 1 }}
									required
								/>
							) : (
								<>
									<TextField
										label="Raw Weight"
										type="number"
										value={rawQuantity}
										onChange={(e) => setRawQuantity(e.target.value)}
										variant="outlined"
										inputProps={{ step: 10 }}
										required
									/>
									<TextField
										label="Cooked Weight"
										type="number"
										value={cookedQuantity}
										onChange={(e) => setCookedQuantity(e.target.value)}
										variant="outlined"
										inputProps={{ step: 10 }}
										required
									/>
								</>
							)}
							<Button variant="contained" onClick={handleAddIngredient}>
								{isEditMode ? "Update" : "Save"}
							</Button>
						</Box>
					</Box>
				</Modal>

				<Snackbar
					open={snackbar.open}
					autoHideDuration={6000}
					onClose={handleCloseSnackbar}
					anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
					<Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
						{snackbar.message}
					</Alert>
				</Snackbar>

				{/* Edit Meal Modal */}
				<Modal
					open={isEditMealModalOpen}
					onClose={handleCloseEditMealModal}
					aria-labelledby="edit-meal-modal-title"
					sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
					<Box sx={{ width: "80vw", maxWidth: 400, bgcolor: "background.paper", p: 4, borderRadius: 2, boxShadow: 24 }}>
						<Typography id="edit-meal-modal-title" variant="h6" gutterBottom>
							Edit Meal
						</Typography>
						<TextField
							fullWidth
							label="Meal Name"
							variant="outlined"
							value={editMealName}
							onChange={(e) => setEditMealName(e.target.value)}
							sx={{ marginBottom: 2 }}
							required
						/>
						<TextField
							fullWidth
							label="Sell Price (RRP)"
							variant="outlined"
							type="number"
							value={editSellPrice}
							onChange={(e) => setEditSellPrice(e.target.value)}
							sx={{ marginBottom: 2 }}
							slotProps={{
								input: {
									startAdornment: <InputAdornment position="start">$</InputAdornment>
								}
							}}
						/>
						<TextField
							fullWidth
							label="Wholesale Price"
							variant="outlined"
							type="number"
							value={editWholesalePrice}
							onChange={(e) => setEditWholesalePrice(e.target.value)}
							sx={{ marginBottom: 3 }}
							slotProps={{
								input: {
									startAdornment: <InputAdornment position="start">$</InputAdornment>
								}
							}}
						/>
						<Button variant="contained" color="primary" onClick={handleSubmitEditMeal} fullWidth>
							Save
						</Button>
					</Box>
				</Modal>
			</Box>
		</>
	);
};

export default MealDetailPage;
